import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { PlatformWhatsappInstance } from '@aeris/contracts'
import {
  Badge,
  Button,
  ConfirmDialog,
  Select,
  type StatusTone,
} from '@aeris/ui'

import {
  fetchMessagingServers,
  fetchPlatformWhatsappInstances,
  linkWhatsappInstance,
  unlinkWhatsappInstance,
} from '../lib/api/platform.ts'
import { ApiError } from '../lib/api.ts'
import { formatWaId } from '../lib/inbox.ts'

/**
 * Os WhatsApps desta loja pela WuzAPI (F192, ADR-0039).
 *
 * O servidor tem as instâncias de todos os clientes; aqui se escolhe quais
 * são DESTA loja. Vincular grava uma chave de assinatura na instância, aponta
 * o webhook dela para o Aeris e cria a conta na loja, "aguardando conexão" —
 * quem lê o QR é a loja, em Atendimento → Canais.
 *
 * A WuzAPI aceita UM webhook por instância. Quando o de hoje aponta para outro
 * sistema, a confirmação mostra o host dele: vincular desliga aquele sistema,
 * e isso precisa ser uma decisão, não um efeito colateral. Desvincular devolve
 * o webhook que estava lá antes.
 */

const WEBHOOK_LABELS: Record<PlatformWhatsappInstance['webhook'], string> = {
  none: 'Livre',
  this_account: 'Desta loja',
  other_account: 'De outra loja',
  elsewhere: 'Outro sistema',
}

const WEBHOOK_TONES: Record<PlatformWhatsappInstance['webhook'], StatusTone> = {
  none: 'neutral',
  this_account: 'success',
  other_account: 'danger',
  elsewhere: 'warning',
}

const connectionOf = (instance: PlatformWhatsappInstance): string =>
  instance.loggedIn
    ? instance.connected
      ? 'Conectada'
      : 'Pareada, sem conexão'
    : 'Não pareada'

export const AccountWhatsapp = ({
  tenantId,
}: {
  tenantId: string
}): ReactNode => {
  const queryClient = useQueryClient()
  const [chosenServer, setChosenServer] = useState<string | null>(null)
  const [linking, setLinking] = useState<PlatformWhatsappInstance | null>(null)
  const [unlinking, setUnlinking] = useState<PlatformWhatsappInstance | null>(
    null
  )
  const [error, setError] = useState<string | null>(null)

  const servers = useQuery({
    queryKey: ['platform-messaging-servers'],
    queryFn: async ({ signal }) => await fetchMessagingServers(signal),
  })

  const serverId = chosenServer ?? servers.data?.[0]?.id ?? null

  const instances = useQuery({
    queryKey: ['platform-whatsapp-instances', tenantId, serverId],
    queryFn: async ({ signal }) =>
      await fetchPlatformWhatsappInstances(tenantId, serverId ?? '', signal),
    enabled: serverId !== null,
  })

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['platform-whatsapp-instances', tenantId],
    })
  }

  const report = (cause: unknown): void => {
    setError(
      cause instanceof ApiError
        ? cause.message
        : 'Não foi possível concluir. Tente de novo.'
    )
  }

  const link = useMutation({
    mutationFn: async (instance: PlatformWhatsappInstance) =>
      await linkWhatsappInstance(tenantId, {
        serverId: serverId ?? '',
        instanceId: instance.id,
        replaceWebhook: instance.webhook === 'elsewhere',
      }),
    onSuccess: async () => {
      setError(null)
      await refresh()
    },
    onError: report,
  })

  const unlink = useMutation({
    mutationFn: async (accountId: string) =>
      await unlinkWhatsappInstance(tenantId, accountId),
    onSuccess: async () => {
      setError(null)
      await refresh()
    },
    onError: report,
  })

  const serverRows = servers.data ?? []

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
        WhatsApp por aparelho vinculado
      </h3>

      {servers.data !== undefined && serverRows.length === 0 ? (
        <p className="text-(length:--text-sm) text-(--text-muted)">
          Nenhum servidor de WhatsApp cadastrado. Cadastre em “Servidores de
          WhatsApp”, no topo do console.
        </p>
      ) : null}

      {serverRows.length > 1 ? (
        <Select
          aria-label="Servidor de WhatsApp"
          value={serverId ?? ''}
          onChange={(event) => {
            setChosenServer(event.target.value)
          }}
          className="w-64"
        >
          {serverRows.map((server) => (
            <option key={server.id} value={server.id}>
              {server.name}
            </option>
          ))}
        </Select>
      ) : null}

      {error === null ? null : (
        <p role="alert" className="text-(length:--text-xs) text-(--danger)">
          {error}
        </p>
      )}

      {instances.isError ? (
        <p role="alert" className="text-(length:--text-xs) text-(--danger)">
          {instances.error instanceof ApiError
            ? instances.error.message
            : 'Não foi possível ler as instâncias do servidor.'}
        </p>
      ) : null}

      {instances.data === undefined ? null : instances.data.length === 0 ? (
        <p className="text-(length:--text-sm) text-(--text-muted)">
          O servidor não tem instâncias.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-(--border-subtle) rounded-(--radius-md) border border-(--border-default)">
          {instances.data.map((instance) => (
            <li
              key={instance.id}
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
            >
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
                  {instance.name}
                </span>
                <span className="text-(length:--text-xs) text-(--text-muted)">
                  {instance.phone === null
                    ? connectionOf(instance)
                    : `${formatWaId(instance.phone)} · ${connectionOf(instance)}`}
                  {instance.webhook === 'other_account' &&
                  instance.linkedTenantName !== null
                    ? ` · ${instance.linkedTenantName}`
                    : ''}
                  {instance.webhook === 'elsewhere' &&
                  instance.webhookHost !== null
                    ? ` · ${instance.webhookHost}`
                    : ''}
                </span>
              </span>
              <span className="flex items-center gap-2">
                <Badge tone={WEBHOOK_TONES[instance.webhook]}>
                  {WEBHOOK_LABELS[instance.webhook]}
                </Badge>
                {instance.webhook === 'this_account' &&
                instance.linkedAccountId !== null ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={
                      unlink.isPending &&
                      unlink.variables === instance.linkedAccountId
                    }
                    onClick={() => {
                      setUnlinking(instance)
                    }}
                  >
                    Desvincular
                  </Button>
                ) : instance.webhook === 'other_account' ? null : (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={
                      link.isPending && link.variables.id === instance.id
                    }
                    onClick={() => {
                      setLinking(instance)
                    }}
                  >
                    Vincular
                  </Button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={linking !== null}
        onOpenChange={(open) => {
          if (!open) setLinking(null)
        }}
        title={linking === null ? '' : `Vincular ${linking.name} a esta loja?`}
        description={
          linking === null
            ? ''
            : linking.webhook === 'elsewhere'
              ? `Hoje esta instância entrega as mensagens para ${linking.webhookHost ?? 'outro sistema'}. Vincular aponta o webhook para o Aeris, e aquele sistema para de receber. Desvincular depois devolve o endereço de hoje.`
              : 'O Aeris passa a receber as mensagens desta instância. A loja conecta o celular em Atendimento → Canais.'
        }
        confirmLabel={
          linking?.webhook === 'elsewhere' ? 'Trocar e vincular' : 'Vincular'
        }
        tone={linking?.webhook === 'elsewhere' ? 'danger' : 'neutral'}
        onConfirm={() => {
          const target = linking
          setLinking(null)
          if (target !== null) link.mutate(target)
        }}
      />

      <ConfirmDialog
        open={unlinking !== null}
        onOpenChange={(open) => {
          if (!open) setUnlinking(null)
        }}
        title={
          unlinking === null ? '' : `Desvincular ${unlinking.name} desta loja?`
        }
        description="O Aeris para de receber e de enviar por este número, e o webhook que estava na instância antes do vínculo volta. As conversas ficam na loja."
        confirmLabel="Desvincular"
        tone="danger"
        onConfirm={() => {
          const target = unlinking
          setUnlinking(null)
          if (target?.linkedAccountId != null) {
            unlink.mutate(target.linkedAccountId)
          }
        }}
      />
    </div>
  )
}
